import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Paper,
  Grid,
  Chip,
  Alert,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { poService } from "../../services/po.service";
import { grnService } from "../../services/grn.service";
import { PurchaseOrder, ReceivePORequest } from "../../types/po.types";

interface ReceiveItemsDialogProps {
  open: boolean;
  onClose: () => void;
  po: PurchaseOrder | null;
  onSuccess: () => void;
}

export const ReceiveItemsDialog: React.FC<ReceiveItemsDialogProps> = ({
  open,
  onClose,
  po,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [hasExistingGRN, setHasExistingGRN] = useState(false);
  const [existingGRN, setExistingGRN] = useState<any>(null);

  // Check if PO already has a GRN when dialog opens
  React.useEffect(() => {
    if (open && po) {
      checkExistingGRN();
    }
  }, [open, po]);

  const checkExistingGRN = async () => {
    if (!po) return;

    setLoading(true);
    try {
      const response = await grnService.checkPOGRN(po._id);
      if (response.success && response.data?.hasGRN) {
        setHasExistingGRN(true);
        setExistingGRN(response.data.grn);
      } else {
        setHasExistingGRN(false);
        setExistingGRN(null);
      }
    } catch (error: any) {
      console.error("Error checking existing GRN:", error);
      // Don't show error for this check, just assume no GRN exists
      setHasExistingGRN(false);
      setExistingGRN(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGRN = () => {
    if (!po) return;

    // Mark PO as received first, then navigate to GRN creation
    markPOAsReceived().then(() => {
      navigate(`/business/procurement/grn/new?po_id=${po._id}`);
      onClose();
    });
  };

  const markPOAsReceived = async () => {
    if (!po) return;

    try {
      const receiveData: ReceivePORequest = {
        _id: po._id,
        received_notes: `Items received. GRN to be created for proper inventory tracking.`,
      };

      const response = await poService.receivePO(receiveData);
      if (response.success) {
        toast.success(
          "PO marked as received. Please create GRN to complete the process."
        );
        onSuccess();
      } else {
        throw new Error(
          response.error?.message || "Failed to mark PO as received"
        );
      }
    } catch (error: any) {
      console.error("Error marking PO as received:", error);
      toast.error(error.message || "Failed to mark PO as received");
      throw error;
    }
  };

  const handleViewExistingGRN = () => {
    if (existingGRN) {
      navigate(`/business/procurement/grn/${existingGRN._id}/edit`);
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
  };

  const formatCurrency = (amount: number, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Receive Items</DialogTitle>

      <DialogContent sx={{ minHeight: 400 }}>
        {po && (
          <Box>
            {/* PO Information */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: "#F8FAFC" }}>
              <Typography variant="subtitle2" gutterBottom>
                Purchase Order Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    PO Number
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {po.po_number}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Supplier
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {po.vendor_details.supplier_name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Total Amount
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatCurrency(po.total_amount, po.currency)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Status
                  </Typography>
                  <Chip label="Ordered" color="primary" size="small" />
                </Grid>
              </Grid>
            </Paper>

            {/* Items List */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Items to Receive ({po.items.length})
              </Typography>
              {po.items.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1.5,
                    borderBottom:
                      index < po.items.length - 1
                        ? "1px solid #E5E7EB"
                        : "none",
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {item.item_name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {item.quantity} {item.uom} ×{" "}
                      {formatCurrency(item.unit_price, po.currency)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="body2" fontWeight={500}>
                      {formatCurrency(item.total_price, po.currency)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Qty: {item.quantity}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Paper>

            {/* Delivery Information */}
            {po.delivery_terms && (
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Delivery Terms
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {po.delivery_terms}
                </Typography>
              </Paper>
            )}

            {/* GRN Status */}
            {loading ? (
              <Alert severity="info">
                Checking for existing Goods Receipt Note...
              </Alert>
            ) : hasExistingGRN ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                This Purchase Order already has a Goods Receipt Note (GRN). You
                can view or edit the existing GRN.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                To properly receive these items and update inventory, you need
                to create a Goods Receipt Note (GRN). This will allow you to
                specify quantities received, batch numbers, expiry dates, and
                item conditions.
              </Alert>
            )}

            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {hasExistingGRN
                ? "You can view or edit the existing GRN to modify receipt details."
                : "The GRN process will mark this PO as received and create proper inventory entries."}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {hasExistingGRN ? (
          <Button
            onClick={handleViewExistingGRN}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            View Existing GRN
          </Button>
        ) : (
          <LoadingButton
            onClick={handleCreateGRN}
            loading={loading}
            variant="contained"
            color="success"
          >
            Create GRN
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};
